import { API_URL_CORE_V1 } from '@webapp/common/conf';

import { ApiRespCode } from './api';

type RequestDelayFunction = (attempt: number, error: Error | null, response: Response | null) => number;

export interface RequestInitWithRetry extends RequestInit {
    retries?: number;
    retryDelay?: number | RequestDelayFunction;
    log?: boolean;
}

const defaultOptions: RequestInitWithRetry = {
    retries: 3,
    retryDelay: 1000,
    log: false,
};

let pendingRequests = 0;
export const hasPendingRequests = (): boolean => pendingRequests > 0;

export const getFormattedParams = (params: Record<string, any>) => {
    let formattedParams = '';

    if (params) {
        const newParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    // @ts-ignore
                    newParams.append(key, value);
                }
            } else if (value) {
                newParams.append(key, value);
            }
        });

        formattedParams = `?${newParams}`;
    }

    return formattedParams;
};

export const ttFetch = (
    options: Partial<RequestInitWithRetry> = {},
): ((url: string, input?: RequestInitWithRetry) => Promise<Response | JsonValue>) => {
    const { headers, keepalive, method, retries, retryDelay, credentials } = Object.assign({}, defaultOptions, options);

    return (url: string, input) => {
        const form = input.body instanceof FormData;

        input = {
            method,
            headers: Object.assign({}, headers, input.headers),
            body: form ? input.body : JSON.stringify(input.body),
            keepalive,
            credentials,
        };

        if (form) {
            delete input.headers['Content-Type'];
        }

        return new Promise((res, rej) => {
            const wrappedFetch = (attempt: number): void => {
                pendingRequests++;

                try {
                    fetch(url, input)
                        .then(async (response) => {
                            const r = response.clone();
                            let body = null;
                            let srvCode = null;
                            let status = null;

                            if (r.status === 302) {
                                window.location.href = r.headers.get('Location');
                            }

                            try {
                                body = await r.json();
                                srvCode = body?.code;
                                status = r?.status;
                            } catch (e) {
                                // Не-JSON ответ (например, HTML-страница ошибки nginx) — это среда/деплой,
                                // не шумим console.error'ом в Sentry.
                                console.warn(`Error on r.json() for endpoint "${url}"`, e);
                                try {
                                    console.warn(
                                        'Error, but can response.text()',
                                        e,
                                        response.status,
                                        await response.text(),
                                    );
                                } catch (e1) {
                                    console.warn('Error on response.text()', e, response.status, e1);
                                }
                                rej(new Error('ошибка ответа сервера'));
                            }

                            if (!r.ok) {
                                // 4xx — ожидаемые клиентские ответы (кэш-промахи, валидация, тестовые данные):
                                // только warn, иначе Sentry плодит группу на каждый запрос с уникальным payload.
                                // 5xx — реальные сбои: error, но со стабильным сообщением (без query/payload),
                                // чтобы группы не дробились по уникальному ключу или телу запроса.
                                const endpoint = url.split('?')[0];
                                const message = `Error on response.status ${r.status} for endpoint "${endpoint}"`;

                                if (r.status >= 500) {
                                    console.error(message);
                                } else {
                                    console.warn(message, body);
                                }
                            }
                            if (
                                !response.ok &&
                                attempt < retries &&
                                srvCode !== null &&
                                srvCode !== ApiRespCode.ERR_ANSWERED_RESPONSE
                            ) {
                                retry(attempt, null, response);
                            } else {
                                res({
                                    status,
                                    url,
                                    ...body,
                                });
                            }
                        })
                        .catch((error) => {
                            if (attempt < retries) {
                                retry(attempt, error, null);
                            } else {
                                console.error(`Error in catch block for endpoint "${url}" and input`, error);
                                rej(new Error('ошибка сети'));
                            }
                        })
                        .finally(() => {
                            pendingRequests--;
                        });
                } catch (e) {}
            };

            function retry(attempt, error, response): void {
                const delay = typeof retryDelay === 'function' ? retryDelay(attempt, error, response) : retryDelay;
                setTimeout(function () {
                    wrappedFetch(++attempt);
                }, delay);
            }

            wrappedFetch(1);
        });
    };
};

export const getApiUrlCoreV1WithParams = (url: string, params?: Record<string, any>) => {
    return `${API_URL_CORE_V1}${url[0] === '/' ? '' : '/'}${url}${getFormattedParams(params)}`;
};
